﻿import type { Strings } from './strings.en.js';

/**
 * Spanish UI strings.
 *
 * Neutral Latin American and peninsular Spanish, using "tú" rather than "usted": the
 * product is a quick, friendly utility shared between friends, and "usted" would read as
 * a bank. Avoids vocabulary that differs sharply between regions.
 *
 * Fixed vocabulary, used consistently:
 *   "límite común"      the group-safe cap
 *   "máximo cómodo"     the comfortable maximum
 *   "sala"              the room
 */
export const es: Strings = {
  common: {
    skipToContent: 'Saltar al contenido principal',
    loading: 'Cargando…',
    tryAgain: 'Intentar de nuevo',
    close: 'Cerrar',
    perPerson: 'por persona',
    navHowItWorks: 'Cómo funciona',
    navPrivacy: 'Privacidad',
    navFaq: 'Preguntas',
    navGuides: 'Guías',
    navBlog: 'Artículos',
    homeLink: 'Inicio',
    language: 'Idioma',
    chooseLanguage: 'Elegir idioma',
    amountInCurrency: (code: string) => `Importe en ${code}`,
    navLandmarkMain: 'Principal',
    navLandmarkFooter: 'Pie de página',
  },

  landing: {
    heroHeadline: 'Acuerden un presupuesto sin que nadie revele el suyo.',
    heroSupport:
      'Crea un enlace privado. Cada persona indica lo máximo que se sentiría cómoda gastando. Solo verás la cantidad con la que todo el grupo puede vivir.',
    previewTitle: 'Lo que ve el grupo',
    previewNote: 'Redondeado a la baja desde el máximo cómodo más bajo.',
    previewExampleNote: 'Solo un ejemplo. No se ha creado ninguna sala.',
  },

  createForm: {
    legend: 'Crear un presupuesto de grupo privado',
    titleLabel: '¿Para qué es el presupuesto?',
    titlePlaceholder: 'Fin de semana en Lisboa',
    titleHint: 'Todos los que tengan el enlace lo verán. No pongas nada privado.',
    peopleLabel: '¿Cuántas personas?',
    peopleHint: 'Entre 3 y 30, incluyéndote.',
    currencyLabel: 'Moneda',
    amountLabel: 'Tu máximo cómodo por persona',
    amountHint: 'Lo máximo que gastarías a gusto. Ni una promesa ni un compromiso.',
    submit: 'Crear presupuesto privado',
    submitting: 'Creando tu enlace privado…',
    trustLine: 'Tu cantidad nunca se muestra al grupo.',
    errorTitleRequired: 'Añade una descripción breve para que se sepa de qué se trata.',
    errorTitleTooLong: 'Que no pase de 80 caracteres.',
    errorPeopleRange: 'Elige entre 3 y 30 personas. Tres es el mínimo para mantener la privacidad.',
    errorAmountRequired: 'Indica lo máximo que te sentirías cómodo gastando.',
    errorAmountInvalid: 'Escribe una cantidad como número, por ejemplo 300.',
    errorAmountTooPrecise: 'Esta moneda no usa tantos decimales.',
    errorAmountTooLarge: 'Esa cantidad es demasiado alta.',
    errorGeneric:
      'Algo salió mal al crear la sala. Tus datos siguen aquí, así que puedes intentarlo otra vez.',
    errorRateLimited: 'Son muchas salas en poco tiempo. Espera un minuto e inténtalo de nuevo.',
    errorTurnstile: 'No pudimos verificar este navegador. Recarga la página e inténtalo otra vez.',
    amountEcho: (formatted: string) => `Es decir, ${formatted}`,
  },

  share: {
    title: 'Tu enlace de presupuesto privado está listo.',
    support:
      'Envíaselo a todas las personas que deban participar. El resultado aparece cuando llegan todas las respuestas esperadas.',
    linkLabel: 'Enlace de la sala privada',
    copy: 'Copiar enlace',
    copied: 'Enlace copiado',
    copyFailed: 'No se pudo copiar automáticamente. Selecciona el enlace de arriba y cópialo.',
    share: 'Compartir enlace',
    shareTitle: 'Presupuesto de grupo privado',
    shareText:
      'Indica lo máximo que te sentirías cómodo gastando por persona. Tu cantidad queda en privado.',
    manageTitle: 'Guarda tu enlace de organizador',
    manageBody:
      'La dirección de esta página también es tu enlace de organizador. Guárdala en favoritos. Es la única forma de cambiar el tamaño del grupo, reiniciar o borrar la sala, y no se puede recuperar.',
    manageWarning: 'No envíes el enlace de organizador al grupo. Envía el enlace de arriba.',
    continue: 'Ir a la sala',
  },

  room: {
    subtitle: 'Presupuesto de grupo privado',
    question: '¿Cuánto es lo máximo que te sentirías cómodo gastando por persona?',
    questionSupport: 'Solo se compartirá el límite común final.',
    amountLabel: 'Tu máximo cómodo por persona',
    submit: 'Enviar en privado',
    submitting: 'Guardando…',
    update: 'Actualizar mi cantidad',
    edit: 'Editar mi cantidad',
    cancelEdit: 'Cancelar',
    savedTitle: 'Respuesta guardada. Puedes editarla hasta que la sala se complete.',
    yourAmount: 'Tu cantidad',
    privacyTitle: 'Privado por diseño',
    privacyPoints: [
      'Sin nombre ni correo',
      'Tu cantidad nunca se muestra',
      'Las cantidades se borran tras el resultado',
    ],
    privacySentence: 'Tu cantidad nunca se muestra al grupo.',
    progressLabel: (answered: number, expected: number) =>
      `${answered} de ${expected} personas han respondido`,
    progressAccessible: (answered: number, expected: number) =>
      `Progreso: ${answered} respuestas recibidas de ${expected} esperadas. No se registra ningún nombre.`,
    editHint: 'Puedes editar tu respuesta hasta que la sala se complete.',
    waitingTitle: 'Esperando al resto del grupo',
    waitingBody: 'El límite común aparecerá aquí en cuanto todos hayan respondido.',
    refresh: 'Buscar novedades',
    revealing: 'Calculando el límite común…',
    fullTitle: 'Este presupuesto ya está completo.',
    fullBody: 'No se pueden añadir más respuestas. Pídele el resultado a quien creó la sala.',
    errorSubmit: 'Tu cantidad no se guardó. No se envió nada al grupo, así que inténtalo otra vez.',
    errorAmountInvalid: 'Escribe una cantidad como número, por ejemplo 300.',
    errorAmountRequired: 'Indica lo máximo que te sentirías cómodo gastando.',
    errorRateLimited: 'Demasiados intentos. Espera un momento y vuelve a enviar.',
    errorConflict:
      'La sala cambió mientras escribías. La hemos actualizado, revísala antes de enviar.',
  },

  result: {
    heading: 'El grupo puede planificar con seguridad hasta',
    eyebrow: 'LÍMITE COMÚN',
    meaningTitle: 'Qué significa esto',
    explanation:
      'Esta cantidad está redondeada a la baja y nunca supera el máximo cómodo más bajo que se envió.',
    privacyTitle: 'Privacidad',
    deletion: 'Las respuestas individuales nunca se mostraron y ya han sido borradas.',
    repeatCta: 'Crear uno para tu grupo',
    footnote: 'Sin nombres · Sin cuentas · Salas temporales',
    availableUntil: (date: string) =>
      `Este resultado estará disponible hasta el ${date} y después se borrará.`,
  },

  manage: {
    title: 'Controles de organizador',
    subtitle: 'Solo visibles para ti, desde tu enlace de organizador.',
    peopleLabel: 'Número de personas previsto',
    increaseHelp: 'Puedes aumentarlo en cualquier momento antes de que aparezca el resultado.',
    decreaseBlocked:
      'Ya no puedes reducirlo porque otra persona ha respondido. Reinicia la sala en su lugar.',
    save: 'Actualizar tamaño del grupo',
    saving: 'Actualizando…',
    saved: 'Tamaño del grupo actualizado.',
    resetTitle: 'Reiniciar la sala',
    resetBody: 'Borra todas las respuestas, incluida la tuya, y empieza de cero.',
    reset: 'Reiniciar sala',
    resetConfirm: '¿Reiniciar esta sala? Todas las respuestas, incluida la tuya, se borran al instante.',
    resetting: 'Reiniciando…',
    resetDone: 'Sala reiniciada. Se borraron todas las respuestas.',
    deleteTitle: 'Borrar la sala',
    deleteBody: 'Elimina la sala y todas las respuestas de inmediato. El enlace deja de funcionar.',
    delete: 'Borrar sala',
    deleteConfirm:
      '¿Borrar esta sala definitivamente? Todas las respuestas se borran al instante y el enlace deja de funcionar.',
    deleting: 'Borrando…',
    deletedTitle: 'Sala borrada.',
    deletedBody: 'La sala y todas las cantidades enviadas han sido borradas.',
    errorGeneric: 'No funcionó. Recarga tu enlace de organizador e inténtalo de nuevo.',
    errorAuth: 'Este enlace de organizador no es válido para esta sala.',
  },

  states: {
    notFoundTitle: 'Este enlace no funciona.',
    notFoundBody:
      'Puede que quien creó la sala la haya borrado, o que el enlace esté incompleto. Revisa el enlace que te enviaron.',
    expiredTitle: 'Esta sala de presupuesto privado caducó y las cantidades enviadas se borraron.',
    expiredBody: 'Las salas duran 7 días. No se conserva nada de esta.',
    createNew: 'Crear un presupuesto nuevo',
    genericErrorTitle: 'Algo salió mal.',
    genericErrorBody: 'No se pudo cargar la sala. Revisa tu conexión e inténtalo de nuevo.',
    offline: 'Parece que no tienes conexión.',
  },

  footer: {
    tagline: 'Una forma discreta de que un grupo acuerde un único límite de gasto.',
    rights: 'Sin cuentas. Sin nombres. Salas temporales.',
    madeBy: (maker: string) => `Hecho por ${maker}`,
    groupTrips: 'Viajes en grupo',
    groupGifts: 'Regalos comunes',
  },

  ads: {
    label: 'Publicidad',
    inactive: (slot: string) => `Espacio publicitario reservado (${slot}), inactivo`,
  },
};
